import { AuthService } from './core/services/auth.service';
import { LoadingService } from './core/services/loading.service';
import { VersionCheckService } from './core/services/version-check.service';
import { UserService } from './core/services/open-api/user.service';
import { Component, OnInit } from '@angular/core';
import { CurrentUser } from './core/models/user';
import { NavigationEnd, Router } from '@angular/router';
import { ThemeService } from '@/core/services/theme.service';
import { TranslationManagerService } from '@/core/services/translation-manager.service';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { TrackingService } from '@/core/services/tracking.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit {
    readonly isLoading$: Observable<boolean>;
    initDone = false;
    language = 'en';
    user?: CurrentUser;
    apiAvailable = true;

    constructor(
        private readonly userService: UserService,
        private readonly loadingService: LoadingService,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly trackingService: TrackingService,
        private versionCheckService: VersionCheckService,
        private readonly themeService: ThemeService,
        private readonly translationManager: TranslationManagerService,
    ) {
        this.translationManager.initTranslations();
        this.isLoading$ = this.loadingService.isLoadingTpl$;
    }

    validateInitLoop(): void {
        if (this.initDone) {
            return;
        }
        this.apiAvailable = false;
        this.initLoadUser();

        setTimeout(() => {
            this.validateInitLoop();
        }, 10000);
    }

    ngOnInit(): void {
        setTimeout(() => {
            this.validateInitLoop();
        }, 3000);
        this.themeService.configureTheme();

        this.versionCheckService.initVersionCheck();

        this.authService.loggedIn$.subscribe((isLoggedIn) => {
            this.initDone = false;
            if (!isLoggedIn) {
                this.initDone = true;
                this.apiAvailable = true;
                return;
            }
            this.initLoadUser();
        });

        this.router.events.subscribe((item) => {
            if (item instanceof NavigationEnd) {
                void this.trackingService.pushGtmTag({
                    event: 'page',
                    pageName: item.url,
                });
            }
        });
    }

    initLoadUser(): void {
        this.userService.loadCurrentUser().subscribe({
            next: (user: CurrentUser) => {
                this.user = user;
                this.setColorTheme(user);
                this.setUserLanguage(user);
                this.initDone = true;
                this.apiAvailable = true;
            },
            error: (err) => {
                // the following may happen if the jwt is invalid
                if (err.status === 401) {
                    this.authService.logout();
                    window.location.reload();
                }
            },
        });
    }

    setColorTheme(user: CurrentUser): void {
        const colorScheme = user.settings.color ?? 'light';
        this.themeService.useSystemTheme = colorScheme === 'system';
        this.themeService.changeColorScheme(colorScheme);
    }

    setUserLanguage(user: CurrentUser): void {
        this.translationManager.setUserLanguage(user);
    }

    /**
     * Link for calling executeSimulators() function,
     * which is the hotspot for simulating complex / difficult to produce scenarios.
     */
    public async simulate(): Promise<void> {
        // IMPORTANT NOTE:
        // The simulation code must not be reachable in prod!
        if (environment.production) {
            return;
        }
        const { executeSimulators } = await import('../test/live-utils/integration-rate-limit.simulator');
        await executeSimulators();
    }

    public get isProduction(): boolean {
        return environment.production;
    }
}
